import Grid from '@mui/material/Grid2';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { downloadFile, urlToBase64 } from '@/utils';
import {
  File,
  FileNameTypes,
  FileTypes,
  FileUploadStatus,
  GetAssemblyDetailsQuery,
  ImageType,
  useDeleteFilesMutation,
  useUpdateFileStatusMutation,
} from '@/__generated__/graphql';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { FileDisplay } from './FileDisplay/';
import { ListFilesSkeleton } from './ListFilesSkeleton';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useAssemblyAttachmentCaption } from '@/hooks/useAssemblyAttachmentCaption';
import EditIcon from '@mui/icons-material/Edit';
import { useSetAtom } from 'jotai';
import {
  sideBySideModeEnabled,
  selectedIsometricFileId,
  drawerLeft,
} from '@/components/Analysis/state';
import { IconButton, Stack, Typography } from '@mui/material';

type Props = {
  referenceId: string;
  collectionName: string;
  files: File[];
  attachments: Attachment[];
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  enableUpload?: boolean;
  onUploadComplete?: (index: number, file: Attachment) => void;
  showRemoveIcon?: boolean;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDialog?: (isOpen: boolean) => void;
  handleRemoveAttachment?: (index: number) => void;
  allFiles: File[];
  location?: string;
  assembly?: GetAssemblyDetailsQuery['assembly'];
};

export const ListFiles = ({
  referenceId,
  collectionName,
  files,
  attachments,
  setAttachments,
  setIsUpdating,
  setOpenDialog,
  allFiles,
  location = '',
  assembly,
}: Props) => {
  const [UpdateFileStatus] = useUpdateFileStatusMutation();
  const { updateAssemblyAttachmentCaption } = useAssemblyAttachmentCaption();
  const [deleteFiles] = useDeleteFilesMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const setSideBySideModeEnabled = useSetAtom(sideBySideModeEnabled);
  const setSelectedIsometricFileId = useSetAtom(selectedIsometricFileId);
  const setDrawerLeft = useSetAtom(drawerLeft);
  const [loading, setLoading] = useState(false);

  const validFiles = useMemo(() => {
    if (!files) return [];
    return files.filter(
      (file) =>
        file.uploadStatus &&
        [FileUploadStatus.Success, FileUploadStatus.Inprogress].includes(file.uploadStatus)
    );
  }, [files]);

  const processFileToAttachment = useCallback(
    async ({
      fileName,
      resourcePath,
      type,
      id,
      uploadStatus,
      caption,
      imageType,
      uuid,
      name,
    }: File) => {
      const fileUrl = resourcePath ? getCloudfrontUrl(resourcePath) : '';
      const isImage =
        type.includes('image/') || [FileTypes.Jpeg, FileTypes.Png].includes(type as FileTypes);

      let base64;
      if (isImage && fileUrl) {
        try {
          base64 = await urlToBase64(fileUrl);
        } catch (error) {
          console.error(`Error loading image for ${fileName}:`, error);
          // Continue with undefined base64, the UI should handle this case
        }
      }

      return {
        id: id || '',
        name: fileName,
        base64,
        type,
        caption: caption || undefined,
        isNew: false,
        url: fileUrl,
        uploadStatus: uploadStatus || FileUploadStatus.Success,
        imageType: imageType || ImageType.Other,
        uuid: uuid || undefined,
        fileName,
        fileNameType: name,
      };
    },
    []
  );

  const fetchImageURLs = useCallback(async () => {
    if (validFiles?.length > 0) {
      setLoading(true);
      try {
        const fetchedFiles = await Promise.all(validFiles.map(processFileToAttachment));
        setAttachments(fetchedFiles);
      } catch (error) {
        console.error('Error processing files:', error);
      } finally {
        setLoading(false);
      }
    }
  }, [validFiles, processFileToAttachment, setAttachments]);

  // Track the previous files fingerprint to detect changes
  const [prevFilesFingerprint, setPrevFilesFingerprint] = useState<string>('');

  useEffect(() => {
    // Only proceed if we have valid files
    if (validFiles?.length > 0) {
      // Create a fingerprint of the current files to detect changes
      const currentFingerprint = validFiles.map((file) => file.id).join(',');

      // Fetch in these cases:
      // 1. Initial load (attachments is empty)
      // 2. Files have changed (different fingerprint)
      if (attachments.length === 0 || currentFingerprint !== prevFilesFingerprint) {
        fetchImageURLs();
        setPrevFilesFingerprint(currentFingerprint);
      }
    }
  }, [validFiles, attachments.length, fetchImageURLs, prevFilesFingerprint]);

  const onUploadComplete = (index: number, file: Attachment) => {
    if (file.isNew && file.resourcePath) {
      const poiAttachmentUrl = getCloudfrontUrl(file.resourcePath);
      setAttachments((previous) => {
        const newAttachments = [...previous];
        newAttachments[index] = {
          ...newAttachments[index],
          url: poiAttachmentUrl,
          isNew: false,
          uploadStatus: FileUploadStatus.Success,
        };
        return newAttachments;
      });
      if (setIsUpdating) setIsUpdating(false);
    }
  };
  const handleDownloadFile = (attachmentIndex: number) => {
    const currentAttachment = attachments[attachmentIndex];
    if (!currentAttachment.url) return;
    downloadFile(currentAttachment.url, currentAttachment.name);
  };

  const handleRemoveFile = (fileIndex: number) => {
    const currentFile = attachments.filter(
      (file) => !file.imageType || file.imageType !== ImageType.WhiteBgPdf
    )[fileIndex];
    if (!currentFile.id) return;
    const filesToRemove: string[] = [];
    filesToRemove.push(currentFile.id);
    const otherFilesToRemove = allFiles?.find(
      (eachFile) =>
        eachFile.id !== currentFile.id &&
        eachFile.uuid === `${currentFile.uuid}-${ImageType.WhiteBgPdf}` &&
        eachFile.imageType === ImageType.WhiteBgPdf
    );
    if (otherFilesToRemove?.id) filesToRemove.push(otherFilesToRemove.id);
    deleteFiles({
      variables: {
        input: {
          referenceId,
          collectionName,
          fileIds: filesToRemove,
        },
      },
    });
    setAttachments(attachments.filter((file) => file.id !== currentFile.id));
  };

  const handelAssemblyAttachmentUpload = async (fileId: string | undefined) => {
    if (!fileId) return;
    await UpdateFileStatus({
      variables: {
        input: {
          referenceId,
          collectionName,
          fileUpdates: [
            {
              uploadStatus: FileUploadStatus.Success,
              fileId,
            },
          ],
        },
      },
      onError: (error) => {
        showSnackBar(error.message, 'error');
      },
    });
  };

  const handelAssemblyImageCaption = async (
    updatedCaption: string | undefined,
    fileId: string | undefined,
    uuid: string | undefined
  ) => {
    if (!fileId) return;
    await updateAssemblyAttachmentCaption(fileId, updatedCaption, referenceId, uuid);

    setAttachments((prev) =>
      prev.map((attachment) =>
        attachment.id === fileId ? { ...attachment, caption: updatedCaption } : attachment
      )
    );
  };

  const handleSBSMode = (attachment?: Attachment) => {
    if (
      !attachment?.id ||
      (attachment.fileNameType !== FileNameTypes.ReferenceDrawing &&
        attachment.fileNameType !== FileNameTypes.IsometricDrawing)
    )
      return;
    setSelectedIsometricFileId(attachment.id);
    setSideBySideModeEnabled(true);
    setDrawerLeft({
      visibility: false,
      query: false,
    });
  };

  const filteredMediaFiles = attachments.filter((file) => {
    if (!file) return false;

    const isImageFile =
      file.type?.includes('image/') ||
      [FileTypes.Jpeg, FileTypes.Png].includes(file.type as FileTypes);

    const isNotWhiteBg = file.imageType !== ImageType.WhiteBgPdf;
    const isFullScreenEnabled = !file.disableFullScreen;

    return isImageFile && isNotWhiteBg && isFullScreenEnabled;
  });

  if (loading) {
    return <ListFilesSkeleton location={location} />;
  }
  return (
    <>
      <Stack direction="row" alignItems="center">
        <Typography variant="h6">Note</Typography>
        <IconButton sx={{ ml: '25rem' }} onClick={() => setOpenDialog && setOpenDialog(true)}>
          <EditIcon />
        </IconButton>
      </Stack>
      <Typography
        variant="body1"
        sx={{
          padding: '12px',
          wordBreak: 'break-word',
          maxWidth: '90%',
          width: '100%',
        }}
      >
        {assembly?.generalAttachmentsNote ?? ''}
      </Typography>
      {!loading && attachments.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" mt={6}>
          <Typography variant="body2" color="primary.300">
            No attachments yet
          </Typography>
        </Stack>
      ) : (
        <Grid
          container
          direction="row"
          spacing={2}
          columnSpacing={'1.6rem'}
          id={`${location}-attachments`}
          columns={3}
        >
          <MediaViewer
            mediaFiles={filteredMediaFiles}
            handleDownloadAttachment={handleDownloadFile}
            handleRemoveAttachment={handleRemoveFile}
          >
            {attachments
              .filter((file) => !file.imageType || file.imageType !== ImageType.WhiteBgPdf)
              .map((file, index) => (
                <Grid key={file.id || `${index}`}>
                  <FileDisplay
                    referenceId={referenceId}
                    collectionName={collectionName}
                    selectedAttachment={file}
                    selectedAttachmentIndex={attachments.indexOf(file)}
                    showRemoveIcon={false}
                    enableUpload={true}
                    onUploadComplete={onUploadComplete}
                    uploadAttachmentHandler={() => handelAssemblyAttachmentUpload(file.id)}
                    imageCaptureHandler={(caption) =>
                      handelAssemblyImageCaption(caption, file.id, file.uuid)
                    }
                    handleClick={(_event, attachment) => {
                      if (
                        attachment?.id &&
                        (attachment.fileNameType === FileNameTypes.ReferenceDrawing ||
                          attachment.fileNameType === FileNameTypes.IsometricDrawing)
                      ) {
                        handleSBSMode(attachment);
                      }
                    }}
                    location={location}
                    handleSBSMode={() => {
                      handleSBSMode(file);
                    }}
                    handleRemoveAttachment={() => handleRemoveFile(attachments.indexOf(file))}
                    handleDownloadAttachment={() => handleDownloadFile(attachments.indexOf(file))}
                  />
                </Grid>
              ))}
          </MediaViewer>
        </Grid>
      )}
    </>
  );
};
